import {
  faArrowRight,
  faArrowUpRight,
} from '@fortawesome/pro-regular-svg-icons'
import {
  ActionIcon,
  Box,
  Flex,
  type PopoverProps,
  Skeleton,
  Text,
  Title,
} from '@mantine/core'
import Link from 'next/link'

import {
  useQueryGetAllBookingProducts,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBooking, type ParameterID } from '@flecto/types'

import {
  AvatarProductsGroup,
  BadgeBookingPaymentRentalStatus,
  BadgeBookingStatus,
  Icon,
  TextCopy,
  TextCurrency,
  TextDate,
} from '..'
import {
  PopoverTracked,
  usePopoverTrackedState,
} from '../PopoverTracked/PopoverTracked'

export type PopoverBookingParams = PopoverProps & {
  bookingId: ParameterID
  popoverId?: string
}

export function PopoverBooking({
  bookingId,
  children,
  popoverId,
}: PopoverBookingParams) {
  const id = popoverId ?? `popover-booking-${bookingId}`

  const { popoverIdOpened } = usePopoverTrackedState()

  const isOpened = id === popoverIdOpened

  const { data: booking, isLoading } = useQueryGetBookingById({
    bookingId,
    options: { enabled: isOpened },
  })

  return (
    <PopoverTracked id={id}>
      <PopoverTracked.Target>{children}</PopoverTracked.Target>
      <PopoverTracked.Dropdown>
        {isLoading ? <ContentLoading /> : <ContentBooking booking={booking} />}
      </PopoverTracked.Dropdown>
    </PopoverTracked>
  )
}

function ContentBooking({ booking }: { booking?: CoreBooking }) {
  const { t } = useTranslation('shared', { keyPrefix: 'shared' })

  if (!booking) {
    return null
  }

  const { data: bookingProducts } = useQueryGetAllBookingProducts({
    bookingId: booking.internal_id,
  })

  const data = [
    {
      label: t('label_customer'),
      value: booking.customer?.full_name ? (
        <Text fw={500} truncate='end'>
          {booking.customer?.full_name}
        </Text>
      ) : (
        '-'
      ),
    },
    {
      label: t('label_phone_number'),
      value: booking.customer?.phone_number ? (
        <TextCopy>{booking.customer?.phone_number}</TextCopy>
      ) : (
        '-'
      ),
    },
    {
      label: t('label_email'),
      value: booking.customer?.email ? (
        <TextCopy>{booking.customer?.email}</TextCopy>
      ) : (
        '-'
      ),
    },
    {
      label: t('label_dates'),
      value: (
        <Flex align='center' data-testid='popover-booking-dates' gap='sm'>
          <TextDate date={booking.start_at} variant='datetime-brief' />
          <Icon icon={faArrowRight} />{' '}
          <TextDate date={booking.end_at} variant='datetime-brief' />
        </Flex>
      ),
    },
    {
      label: t('label_rental_value'),
      value: (
        <Flex
          align='center'
          data-testid='popover-booking-rental-value'
          gap='sm'
        >
          <TextCurrency cents={booking.meta?.items_total_cents ?? 0} />
          <Text>({t('label_excl_taxes')})</Text>
        </Flex>
      ),
    },
    {
      label: t('label_products'),
      value: (
        <AvatarProductsGroup
          products={bookingProducts?.map((bi) => ({
            ...bi.product,
            quantity: bi.quantity,
          }))}
        />
      ),
    },
  ]

  return (
    <Box>
      <Flex align='center' justify='space-between'>
        <Flex gap='sm'>
          <Title order={5}>
            {t('label_booking')} #{booking.internal_id}
          </Title>
          <BadgeBookingStatus
            booking={booking}
            data-testid='popover-booking-status'
          />
          <BadgeBookingPaymentRentalStatus
            booking={booking}
            data-testid='popover-booking-payment-status'
          />
        </Flex>
        <Link href={`/bookings/${booking.internal_id}`} target='_blank'>
          <ActionIcon data-testid='btn-booking' size='sm' variant='default'>
            <Icon icon={faArrowUpRight} size='sm' />
          </ActionIcon>
        </Link>
      </Flex>
      <Box mt='md'>
        {data.map(({ label, value }) => (
          <Flex align='center' key={label} mt='sm' pt='xs' w='100%'>
            <Box style={{ flexShrink: 0, width: 150 }}>{label}</Box>
            <Box>{value}</Box>
          </Flex>
        ))}
      </Box>
    </Box>
  )
}

function ContentLoading() {
  const dataValuesWidths = [200, 250, 220, 190, 210, 230]

  return (
    <Box data-testid='popover-booking-loading'>
      <Flex gap='sm'>
        <Skeleton h={20} w={180} />
        <Skeleton h={20} w={80} />
        <Skeleton h={20} w={100} />
      </Flex>
      <Box mt='lg'>
        {dataValuesWidths.map((width, key) => (
          <Flex align='center' key={key} mt='md' pt='xs' w='100%'>
            <Box style={{ flexShrink: 0, width: 150 }}>
              <Skeleton h={15} w={100} />
            </Box>
            <Skeleton h={15} mt={3} w={width} />
          </Flex>
        ))}
      </Box>
    </Box>
  )
}
